import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Bell, Plus, Trash2, Sparkles } from "lucide-react";

export default function Alerts() {
  const { t } = useI18n();
  const [alerts, setAlerts] = useState([]);
  const [role, setRole] = useState("");
  const [cities, setCities] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/jobs/cities").then((r) => setCityOptions(r.data)).catch(() => {});
  }, []);

  const toggleCity = (c) =>
    setCities((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const create = async () => {
    if (!role.trim()) {
      toast.error("Enter a role");
      return;
    }
    setLoading(true);
    try {
      await api.post("/alerts", { role, cities });
      setRole("");
      setCities([]);
      toast.success("Alert saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save alert");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/alerts/${id}`);
      load();
    } catch {
      toast.error("Could not delete alert");
    }
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="alerts-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">/ {t("alerts")}</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
          Never miss a posting.
        </h1>
        <p className="text-gray-600 mt-2">Save searches — we'll track how many new jobs match each week.</p>
      </div>

      <div className="border border-black/10 bg-[#F8F9FA] p-6 space-y-5">
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Role / keyword</span>
          <input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="e.g. pharmacist"
            className="w-full bg-transparent border-b-2 border-gray-200 focus:border-black outline-none py-3 text-lg"
            data-testid="alert-role"
          />
        </label>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">Cities</div>
          <div className="flex flex-wrap gap-2">
            {cityOptions.map((c) => {
              const active = cities.includes(c);
              return (
                <button
                  key={c}
                  onClick={() => toggleCity(c)}
                  className={`px-3 py-1.5 text-sm border transition-colors ${
                    active ? "bg-black text-white border-black" : "bg-white border-black/20 hover:border-black"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
        <button
          onClick={create}
          disabled={loading}
          className="bg-black text-white hover:bg-[#0033FF] font-bold px-6 py-3 flex items-center gap-2 transition-colors disabled:opacity-60"
          data-testid="alert-create"
        >
          <Plus className="w-4 h-4" /> {loading ? "…" : "Save alert"}
        </button>
      </div>

      <div className="space-y-3" data-testid="alerts-list">
        {alerts.length === 0 ? (
          <div className="border border-dashed border-black/20 p-10 text-center text-gray-500">No alerts yet.</div>
        ) : (
          alerts.map((a) => (
            <div key={a.id} className="border border-black/10 bg-white p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <Bell className="w-5 h-5 text-[#0033FF] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold truncate">{a.role}</div>
                  <div className="text-sm text-gray-500 truncate">
                    {a.cities.length ? a.cities.join(", ") : "All cities"}
                  </div>
                </div>
              </div>
              <div className="text-end flex-shrink-0">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Matches</div>
                <div className="flex items-center gap-1 text-[#34C759] font-bold">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span className="num-ticker">{a.match_count}</span>
                </div>
              </div>
              <button
                onClick={() => remove(a.id)}
                className="p-2 hover:bg-[#FF3B30] hover:text-white border border-black/10"
                data-testid={`alert-delete-${a.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
